import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import WebPdfDocumentPreview from './WebPdfDocumentPreview';
import { downloadBlobFromUrl } from '../../../api/storage';
import { Sentry } from '../../../setupSentry';
import '../../../css/common/pdf/PdfPreview.css';

type PdfPreviewRequest = { previewUrl: string; downloadUrl: string; fileName: string; allowPrint: boolean };
export type PdfPreviewInput = {
  url?: string;
  previewUrl?: string;
  downloadUrl?: string;
  fileName?: string;
  mimeType?: string;
  allowPrint?: boolean;
};
type PdfPreviewContextValue = { openPreview: (_input: PdfPreviewInput) => void };
type PdfBlobCache = { key: string; blob: Blob | null; promise: Promise<Blob> | null };

const PdfPreviewContext = createContext<PdfPreviewContextValue | null>(null);

export const isPdfPreviewable = ({
  fileName = '',
  mimeType = '',
  url = '',
}: PdfPreviewInput = {}): boolean => {
  const normalizedType = String(mimeType || '').toLowerCase();
  return normalizedType.includes('pdf') || /\.pdf(?:$|[?#])/i.test(fileName) || /\.pdf(?:$|[?#])/i.test(url);
};

const ensurePdfFilename = (value = ''): string => {
  const normalized = String(value || '').trim() || 'document.pdf';
  return /\.pdf$/i.test(normalized) ? normalized : `${normalized}.pdf`;
};

const PDF_PREVIEW_ROUTE = /\/(?:api\/)?(?:files\/message_files\/|admin\/messages\/files\/[^/?#]+\/download|artist\/messages\/files\/[^/?#]+\/download|event-chat\/files\/[^/?#]+\/download|contracts\/[^/?#]+\/invoice\/file|artist\/supplemental-invoices\/[^/?#]+\/invoice\/file)/i;

export const withPdfPreviewQuery = (value = ''): string => {
  const url = String(value || '').trim();
  if (!url || !PDF_PREVIEW_ROUTE.test(url) || /[?&]preview=(?:1|true)(?:&|$)/i.test(url)) return url;
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  return `${base}${base.includes('?') ? '&' : '?'}preview=1${hash}`;
};

export const isPdfBlob = async (blob: unknown): Promise<boolean> => {
  if (!(blob instanceof Blob)) return false;
  if (blob.size < 12) return false;
  const prefix = await blob.slice(0, Math.min(blob.size, 1024)).text();
  if (!prefix.includes('%PDF-')) return false;
  const tailStart = Math.max(0, blob.size - 64 * 1024);
  const tail = await blob.slice(tailStart).text();
  const startXrefIndex = tail.lastIndexOf('startxref');
  const eofIndex = tail.lastIndexOf('%%EOF');
  if (startXrefIndex < 0 || eofIndex < startXrefIndex) return false;
  const xrefOffset = Number(tail.slice(startXrefIndex).match(/^startxref\s+(\d+)/)?.[1]);
  return Number.isSafeInteger(xrefOffset) && xrefOffset >= 0 && xrefOffset < blob.size;
};

const saveBlob = (blob: Blob, filename?: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensurePdfFilename(filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const printPdfBlob = (blob: Blob): void => {
  const objectUrl = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.dataset.pdfPrintFrame = 'true';
  frame.title = 'PDF印刷';
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.width = '1px';
  frame.style.height = '1px';
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    frame.remove();
    URL.revokeObjectURL(objectUrl);
  };
  frame.addEventListener('load', () => {
    const printWindow = frame.contentWindow;
    if (!printWindow) {
      cleanup();
      return;
    }
    printWindow.addEventListener('afterprint', cleanup, { once: true });
    printWindow.focus();
    printWindow.print();
    window.setTimeout(cleanup, 60_000);
  }, { once: true });
  frame.src = objectUrl;
  document.body.appendChild(frame);
};

const canShareFile = (file: File | null): boolean => {
  if (!file || typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

export const PdfPreviewProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<PdfPreviewRequest | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const loadSequenceRef = useRef(0);
  const previewCacheRef = useRef<PdfBlobCache>({ key: '', blob: null, promise: null });
  const originalCacheRef = useRef<PdfBlobCache>({ key: '', blob: null, promise: null });

  const closePreview = useCallback(() => {
    loadSequenceRef.current += 1;
    setRequest(null);
    setLoading(false);
    setActionLoading(false);
    setError('');
    setPdfBlob(null);
  }, []);

  const openPreview = useCallback(({ url, previewUrl, downloadUrl, fileName, allowPrint = false }: PdfPreviewInput = {}) => {
    const originalUrl = String(downloadUrl || url || '').trim();
    const normalizedPreviewUrl = String(previewUrl || withPdfPreviewQuery(url || originalUrl)).trim();
    if (!normalizedPreviewUrl || !originalUrl) return;
    setRequest({
      previewUrl: normalizedPreviewUrl,
      downloadUrl: originalUrl,
      fileName: ensurePdfFilename(fileName),
      allowPrint,
    });
  }, []);

  const getPdfBlob = useCallback((currentRequest: PdfPreviewRequest): Promise<Blob> => {
    const key = `${currentRequest.previewUrl}|${currentRequest.fileName}`;
    const cached = previewCacheRef.current;
    if (cached.key === key && cached.blob) return Promise.resolve(cached.blob);
    if (cached.key === key && cached.promise) return cached.promise;

    const promise = (async () => {
      const downloaded = await downloadBlobFromUrl(currentRequest.previewUrl, 'include');
      if (!(await isPdfBlob(downloaded))) {
        Sentry.captureMessage('web_pdf_integrity_validation_failed', {
          level: 'warning',
          tags: { component: 'PdfPreviewProvider', stage: 'download_validation' },
        });
        throw new Error('PDFの取得が完了していないか、PDF形式として確認できませんでした。');
      }
      return downloaded.type === 'application/pdf'
        ? downloaded
        : new Blob([downloaded], { type: 'application/pdf' });
    })();
    previewCacheRef.current = { key, blob: null, promise };
    promise.then((blob) => {
      if (previewCacheRef.current.key === key && previewCacheRef.current.promise === promise) {
        previewCacheRef.current = { key, blob, promise: null };
      }
    }).catch(() => {
      if (previewCacheRef.current.key === key && previewCacheRef.current.promise === promise) {
        previewCacheRef.current = { key: '', blob: null, promise: null };
      }
    });
    return promise;
  }, []);

  const getOriginalBlob = useCallback((currentRequest: PdfPreviewRequest): Promise<Blob> => {
    const key = `${currentRequest.downloadUrl}|${currentRequest.fileName}`;
    const cached = originalCacheRef.current;
    if (cached.key === key && cached.blob) return Promise.resolve(cached.blob);
    if (cached.key === key && cached.promise) return cached.promise;

    const promise = downloadBlobFromUrl(currentRequest.downloadUrl, 'include');
    originalCacheRef.current = { key, blob: null, promise };
    promise.then((blob) => {
      if (originalCacheRef.current.key === key && originalCacheRef.current.promise === promise) {
        originalCacheRef.current = { key, blob, promise: null };
      }
    }).catch(() => {
      if (originalCacheRef.current.key === key && originalCacheRef.current.promise === promise) {
        originalCacheRef.current = { key: '', blob: null, promise: null };
      }
    });
    return promise;
  }, []);

  const loadPdf = useCallback(async () => {
    if (!request?.previewUrl) return;
    const sequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequence;
    setLoading(true);
    setError('');
    setPdfBlob(null);
    try {
      const downloaded = await getPdfBlob(request);
      if (loadSequenceRef.current !== sequence) return;
      setPdfBlob(downloaded);
    } catch (loadError: unknown) {
      if (loadSequenceRef.current !== sequence) return;
      setError(loadError instanceof Error ? loadError.message : 'PDFを読み込めませんでした。');
    } finally {
      if (loadSequenceRef.current === sequence) setLoading(false);
    }
  }, [getPdfBlob, request]);

  useEffect(() => {
    if (request?.previewUrl) loadPdf();
  }, [loadPdf, request?.previewUrl]);

  useEffect(() => () => {
    loadSequenceRef.current += 1;
    previewCacheRef.current = { key: '', blob: null, promise: null };
    originalCacheRef.current = { key: '', blob: null, promise: null };
  }, []);

  const shareSupported = typeof File === 'function'
    && typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function';

  const handleSave = useCallback(async () => {
    if (!request || actionLoading) return;
    setActionLoading(true);
    try {
      saveBlob(await getOriginalBlob(request), request.fileName);
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'PDFを保存できませんでした。');
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, getOriginalBlob, request]);

  const handleShare = useCallback(async () => {
    if (!request || !shareSupported || actionLoading) return;
    setActionLoading(true);
    try {
      const original = await getOriginalBlob(request);
      const shareFile = new File([original], request.fileName, { type: 'application/pdf' });
      if (!canShareFile(shareFile)) throw new Error('この環境ではPDFを共有できません。');
      await navigator.share({ files: [shareFile], title: request.fileName || 'PDF' });
    } catch (shareError: unknown) {
      if (!(shareError instanceof DOMException && shareError.name === 'AbortError')) {
        setError(shareError instanceof Error ? shareError.message : 'PDFを共有できませんでした。');
      }
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, getOriginalBlob, request, shareSupported]);

  const handlePrint = useCallback(async () => {
    if (!request?.allowPrint || actionLoading) return;
    setActionLoading(true);
    try {
      printPdfBlob(await getPdfBlob(request));
    } catch (printError: unknown) {
      setError(printError instanceof Error ? printError.message : 'PDFを印刷できませんでした。');
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, getPdfBlob, request]);

  const contextValue = useMemo(() => ({ openPreview }), [openPreview]);
  const closeAction = (
    <DsButton
      variant="secondary"
      size="sm"
      icon
      aria-label="PDFプレビューを閉じる"
      title="閉じる"
      onClick={closePreview}
    >
      <i className="fa-solid fa-xmark" aria-hidden="true" />
    </DsButton>
  );
  const fileActions = request ? (
    <>
      {request.allowPrint ? (
        <DsButton
          variant="secondary"
          size="sm"
          icon
          aria-label="印刷"
          title="印刷"
          disabled={actionLoading}
          onClick={handlePrint}
        >
          <i className="fa-solid fa-print" aria-hidden="true" />
        </DsButton>
      ) : null}
      {shareSupported ? (
        <DsButton
          variant="secondary"
          size="sm"
          icon
          aria-label="共有"
          title="共有"
          disabled={actionLoading}
          onClick={handleShare}
        >
          <i className="fa-solid fa-share-nodes" aria-hidden="true" />
        </DsButton>
      ) : null}
      <DsButton
        variant="primary"
        size="sm"
        icon
        aria-label="保存"
        title="保存"
        disabled={actionLoading}
        onClick={handleSave}
      >
        <i className="fa-solid fa-download" aria-hidden="true" />
      </DsButton>
      {closeAction}
    </>
  ) : closeAction;

  return (
    <PdfPreviewContext.Provider value={contextValue}>
      {children}
      <DsModal
        open={Boolean(request)}
        onClose={closePreview}
        title={undefined}
        ariaLabel={`${request?.fileName || 'PDF'}のプレビュー`}
        footer={undefined}
        showClose={false}
        size="lg"
        presentation="fullscreen"
        overlayClassName="pdf-preview-overlay"
        className="pdf-preview"
        bodyClassName="pdf-preview__body"
      >
        {loading ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{fileActions}</div>
            </div>
            <div className="pdf-preview__state" role="status">PDFを読み込んでいます...</div>
          </div>
        ) : error ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{fileActions}</div>
            </div>
            <div className="pdf-preview__state pdf-preview__state--error" role="alert">
              <p>{error}</p>
              <p className="pdf-preview__error-guidance">
                表示できない場合も、［保存］から原本のPDFを確認できます。
              </p>
              <DsButton variant="secondary" onClick={loadPdf}>再試行</DsButton>
            </div>
          </div>
        ) : pdfBlob ? (
          <WebPdfDocumentPreview
            blob={pdfBlob}
            fileName={request?.fileName || 'document.pdf'}
            onError={setError}
            toolbarActions={fileActions}
          />
        ) : null}
      </DsModal>
    </PdfPreviewContext.Provider>
  );
};

export const usePdfPreview = () => useContext(PdfPreviewContext);
